/* eslint-disable @next/next/no-img-element */
'use client'
import * as React from 'react'
import { tw } from 'utils/classnames'
import Image from 'next/image'
import { useLocale } from 'next-intl'
import { addOrReplaceLocaleInUrl } from 'cf-i18n/helpers'
import { Link } from '@repo/web-ui/components/link'
import { formatNumberToNumericalAbreviation } from 'utils/number'
import { useWidthResizeObserver } from '@repo/web-ui/hooks/use-resize-observer'

import { useFavoritesContext } from '@web/app/[locale]/(web)/prod/[slug]/favorites-context'

export function Avatars() {
  const { total, users, isLoading } = useFavoritesContext()
  const [containerRef, setContainerRef] = React.useState<HTMLDivElement | null>(null)
  const [showTotal, setShowTotal] = React.useState<number | null>(null)
  const [maxItems, setMaxItems] = React.useState<number>(0)
  const containerWidth = useWidthResizeObserver(containerRef)
  const locale = useLocale()

  const parsedUsers = React.useMemo(
    () =>
      isLoading
        ? []
        : users.map(user => {
            const crafterUrl = user.profileUrls?.crafter?.endsWith('/')
              ? user.profileUrls.crafter
              : `${user.profileUrls?.crafter ?? ''}/`
            const initials = user.profileUrls?.crafter?.split('/').pop()?.charAt(0) ?? ''
            return { ...user, crafterUrl, initials }
          }),
    [isLoading, users],
  )

  React.useEffect(() => {
    const maxItems = Math.floor(containerWidth / 31) || 1
    setMaxItems(maxItems)

    if (maxItems && users.length > maxItems) {
      setShowTotal(Math.max(total - maxItems, 0))
    } else {
      setShowTotal(null)
    }
  }, [isLoading, total, containerWidth, users.length])

  if (!parsedUsers.length) {
    return null
  }

  return (
    <div
      className="text-cf-dark-aubergine relative mb-4 flex h-[36px] w-full justify-start"
      ref={setContainerRef}
    >
      {parsedUsers.slice(0, maxItems).map((user, index) => (
        <UserAvatar
          key={index}
          className="hover:shadow-cf-elevation-light-1 z-0 -mr-[9px] rounded-full"
          left={index * 30}
          crafterUrl={addOrReplaceLocaleInUrl(user.crafterUrl, locale)}
          avatarUrl={user.avatarUrl}
          initials={user.initials}
        />
      ))}
      {showTotal ? (
        <div
          className="bg-cf-white text-13 absolute -ml-1 flex aspect-[1] size-10 place-content-center place-items-center rounded-full border-2 border-[#DFDDE5] font-medium"
          style={{ left: `${parsedUsers.slice(0, maxItems).length * 30}px` }}
        >
          <span className="-ml-1">{`+${formatNumberToNumericalAbreviation(showTotal)}`}</span>
        </div>
      ) : null}
    </div>
  )
}

export interface UserAvatarProps extends React.HTMLAttributes<HTMLAnchorElement> {
  crafterUrl?: string
  avatarUrl?: string | null
  initials?: string
  left?: number
}

export function UserAvatar({
  className,
  crafterUrl,
  avatarUrl,
  initials,
  left,
  ...props
}: UserAvatarProps) {
  function renderAvatar() {
    return (
      <div className="bg-cf-white border-cf-white aspect-[1] size-10 rounded-full border-2 bg-transparent">
        {avatarUrl ? (
          <Image
            src={avatarUrl}
            alt="User avatar"
            width={36}
            height={36}
            className="rounded-full object-cover"
          />
        ) : (
          <div className="text-cf-dreamy-cloud-900 bg-cf-dreamy-cloud-400 flex h-full w-full items-center justify-center font-bold">
            {initials?.toUpperCase()}
          </div>
        )}
      </div>
    )
  }

  return crafterUrl ? (
    <Link
      href={crafterUrl}
      className={tw('absolute z-0 inline-block hover:z-10', className)}
      style={{ left: `${left}px` }}
      target="_blank"
      component="a"
      {...props}
    >
      {renderAvatar()}
    </Link>
  ) : (
    renderAvatar()
  )
}
